import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { articlesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Shield, ArrowLeft, Calendar, User, BookOpen } from 'lucide-react';

export default function ArticlePage() {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(function loadArticle() {
    async function fetchArticle() {
      try {
        const data = await articlesApi.getBySlug(slug);
        setArticle(data);
      } catch (err) {
        console.error('Failed to fetch article:', err);
        setError('Article not found');
      }
      setLoading(false);
    }
    fetchArticle();
  }, [slug]);

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <Shield className="h-8 w-8 text-teal-600" />
                <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  AICreditPilot
                </span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="pt-32 pb-16 max-w-3xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-1/4" />
            <div className="h-64 bg-slate-200 rounded-xl" />
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded" />
              <div className="h-4 bg-slate-200 rounded" />
              <div className="h-4 bg-slate-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-white">
        <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <Shield className="h-8 w-8 text-teal-600" />
                <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  AICreditPilot
                </span>
              </Link>
            </div>
          </div>
        </nav>
        <div className="pt-32 pb-16 max-w-3xl mx-auto px-4 text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <Button onClick={function() { navigate('/blog'); }} className="btn-teal">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="article-page">
      {/* Navigation */}
      <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="h-8 w-8 text-teal-600" />
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                AICreditPilot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-slate-600 hover:text-slate-900 transition-colors">Home</Link>
              <Link to="/blog" className="text-teal-600 font-medium">Blog</Link>
              <Link to="/#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button 
                  onClick={function() { navigate('/dashboard'); }}
                  className="btn-primary"
                >
                  Dashboard
                </Button>
              ) : (
                <Button 
                  onClick={function() { navigate('/register'); }}
                  className="btn-teal"
                >
                  Get Started
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          {/* Back Link */}
          <Button
            variant="ghost"
            onClick={function() { navigate('/blog'); }}
            className="mb-6 text-slate-600 hover:text-slate-900"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          {/* Header */}
          <header className="mb-8">
            <Badge variant="secondary" className="mb-4 bg-teal-50 text-teal-700">
              {article.category}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(article.created_at)}
              </div>
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {article.author_name}
              </div>
            </div>
          </header>

          {/* Featured Image */}
          {article.featured_image && (
            <div className="mb-8 rounded-xl overflow-hidden">
              <img 
                src={article.featured_image} 
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-slate prose-lg max-w-none">
            <div 
              className="text-slate-700 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br/>') }}
            />
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Take Action?
          </h2>
          <p className="text-slate-600 mb-6">
            Let AICreditPilot help you dispute inaccurate items and improve your credit.
          </p>
          <Button 
            onClick={function() { navigate('/register'); }}
            className="btn-teal"
          >
            Start Free Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-500" />
              <span className="font-bold text-white" style={{ fontFamily: 'Manrope' }}>AICreditPilot</span>
            </div>
            <p className="text-slate-500 text-sm text-center">
              © 2026 AICreditPilot. Educational service. No credit score increases guaranteed.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
